import * as _ from 'lodash';
import { getLogger } from '../../diagnostics/logger';
import { getDaysTime, getMonthsTime, getStartOfCurrentMonth, getUtcDaysTime } from '../../api/utilities';
import { HashMap } from '../../constants';
import { SimCommandCreate } from '../simCommand';
import { ESimSmartDetailedBarCommand } from './ESimSmartDetailedBarCommand';
import { ESimSmartDetailedUnbarCommand } from './ESimSmartDetailedUnbarCommand';
import { O2DetailedBarCommand } from './O2DetailedBarCommand';
import { O2DetailedUnbarCommand } from './O2DetailedUnbarCommand';
import { VodaDetailedBarCommand } from './VodaDetailedBarCommand';
import { VodaDetailedUnbarCommand } from './VodaDetailedUnbarCommand';
import {
  BarServiceConfigCommand,
  BaseCommand,
  BasicBarCommand,
  BasicUnbarCommand,
  BtIotUpdateDetailsCommand,
  ChangeAccountCommand,
  ChangeAccountRefCommand,
  ChangeProvisionCommand,
  CheckSerialCommand,
  ConfigureBundleCommand,
  ConfigureFairUsageCommand,
  ConfigureSmsAlertsCommand,
  CreateBundleCommand,
  DeconfigureSmsAlertsCommand,
  EditPoCommand,
  EsimSmartChangeBundleCommand,
  EsimsmartCreateBundleCommand,
  EsimsmartRemoveBundleCommand,
  TopupCommand,
  EsimsmartUsageCommand,
  ExportEsimCommand,
  LiftCapCommand,
  MigrateInCommand,
  MigrateOutCommand,
  MsisdnSwapCommand,
  OrangeLightBundleUsage,
  OrangeModifyBundleCommand,
  OrangeRemoveBundleCommand,
  OrangeResetBundleCommand,
  OrangeTopupCommand,
  OrangeUsageCommand,
  PortInCommand,
  PortOutCommand,
  ProvisionCommand,
  ReconfigureSimProfileCommand,
  RefreshConnectionCommand,
  RemoveBundleCommand,
  RemoveSpendCapCommand,
  SendSmsCommand,
  SimStatusReportCommand,
  SimSwapCommand,
  SpendCapInputCreateCommand,
  SubmissionCommand,
  UnprovisionCommand,
  ConfigureThresholdCommand,
  RemoveImeiLockCommand,
  ResetImeiLockCommand,
  CreateImeiLockCommand,
  AssignAlertManagerCommand,
  RemoveAlertManagerCommand
} from './commands';
import { SubmissionState } from './simProvision/simProvisionState';

const log = getLogger('simManagement.state.submissionDefaults');

export const createSpendCapSubmission: (command: SimCommandCreate) => SpendCapInputCreateCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'configurespendcap'),
    definitionAlias: 'configure-spend-cap',
    name: 'Configure spend caps',
    serialNumbers: [''],
    cap: 0,
    notifyEmailAddresses: [''],
    smsOptOut: false,
    barsToPersist: null
  };
};

export const removeSpendCapSubmission: (command: SimCommandCreate) => RemoveSpendCapCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'removespendcap'),
    definitionAlias: 'remove-spend-cap',
    name: 'Remove spend caps',
    serialNumbers: []
  };
};

export const configureBundleControlSubmission: (command: SimCommandCreate) => ConfigureBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'configureBundleControl'),
    definitionAlias: 'configure-bundle-control',
    name: 'Configure Bundle Sumission',
    serialNumbers: [],
    zone: '',
    bundleControlPeriod: 'Month',
    sizeMb: 0,
    startDate: null,
    endDate: null,
    bundleControlEndAction: '',
    roamingBucketVolumes: []
  } as unknown as ConfigureBundleCommand;
};

export const configureFairUsageSubmission: (command: SimCommandCreate) => ConfigureFairUsageCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'configureFairUsage'),
    definitionAlias: 'configure-fair-usage',
    name: 'Configure Fair Usage Policy',
    serialNumbers: [],
    buckets: []
  };
};

export const RemoveBundleControlSubmission: (command: SimCommandCreate) => RemoveBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'removeBundleControl'),
    definitionAlias: 'remove-bundle-control',
    name: 'Remove Bundle Submission',
    serialNumbers: [],
    actionDate: null
  };
};

export const CreateSendSmsSubmission: (command: SimCommandCreate) => SendSmsCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'sendSms'),
    definitionAlias: 'send-sms',
    serialNumbers: [],
    message: ''
  };
};

const createBasicBarCommand: (command: SimCommandCreate) => BasicBarCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'bar'),
    definitionAlias: 'basic-bar'
  };
};

const createEsimSmartDetailedBarCommand: (command: SimCommandCreate) => ESimSmartDetailedBarCommand = (
  command: SimCommandCreate
) => {
  const settings = getBasicCommandSettings(command, 'bar');
  return new ESimSmartDetailedBarCommand(settings);
};

const createEsimSmartDetailedUnbarCommand: (command: SimCommandCreate) => ESimSmartDetailedUnbarCommand = (
  command: SimCommandCreate
) => {
  const settings = getBasicCommandSettings(command, 'unbar');
  return new ESimSmartDetailedUnbarCommand(settings);
};

const createO2DetailedBarCommand: (command: SimCommandCreate) => O2DetailedBarCommand = (command: SimCommandCreate) => {
  const settings = getBasicCommandSettings(command, 'bar');
  return new O2DetailedBarCommand(settings);
};

const createO2DetailedUnbarCommand: (command: SimCommandCreate) => O2DetailedUnbarCommand = (
  command: SimCommandCreate
) => {
  const settings = getBasicCommandSettings(command, 'unbar');
  return new O2DetailedUnbarCommand(settings);
};

const createLiftCapCommand: (command: SimCommandCreate) => LiftCapCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'uncap'),
    definitionAlias: 'lift-spend-cap'
  };
};

const createVodaDetailedBarCommand: (command: SimCommandCreate) => VodaDetailedBarCommand = (
  command: SimCommandCreate
) => {
  const settings = getBasicCommandSettings(command, 'bar');
  return new VodaDetailedBarCommand(settings);
};

const createVodaDetailedUnbarCommand: (command: SimCommandCreate) => VodaDetailedUnbarCommand = (
  command: SimCommandCreate
) => {
  const settings = getBasicCommandSettings(command, 'unbar');
  return new VodaDetailedUnbarCommand(settings);
};

const createBasicUnbarCommand: (command: SimCommandCreate) => BasicUnbarCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'unbar'),
    definitionAlias: 'basic-unbar'
  };
};

const createCheckSerialCommand: (command: SimCommandCreate) => CheckSerialCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'check the serial of'),
    definitionAlias: 'check-serial'
  };
};

const createSwapCommand: (command: SimCommandCreate) => SimSwapCommand = (command: SimCommandCreate) => {
  const simSwaps: string[] =
    command.stockParams && command.stockParams.length === command.stockIds.length
      ? [..._.map(command.stockParams, (item) => item || '')]
      : Array(command.stockIds.length).fill('');
  return {
    ...getBasicCommandSettings(command, 'swap'),
    definitionAlias: 'sim-swap',
    simSwaps
  };
};

const createMsisdnSwapCommand: (command: SimCommandCreate) => MsisdnSwapCommand = (command: SimCommandCreate) => {
  const swaps: string[] =
    command.stockParams && command.stockParams.length === command.stockIds.length
      ? [..._.map(command.stockParams, (item) => item || '')]
      : Array(command.stockIds.length).fill('');
  return {
    ...getBasicCommandSettings(command, 'swap MSISDNs for'),
    definitionAlias: 'msisdn-swap',
    networkId: command.networkId,
    accountId: command.accountId,
    swaps
  };
};

const createMigrateInCommand: (command: SimCommandCreate) => MigrateInCommand = (command: SimCommandCreate) => {
  const tels =
    command.stockParams && command.stockParams.length === command.stockIds.length
      ? [..._.map(command.stockParams, (item) => item || null)]
      : Array(command.stockIds.length).fill(null);
  return {
    ...getBasicCommandSettings(command, 'migrate-in'),
    definitionAlias: 'migrate-in',
    boltOns: [],
    boltOnsString: '',
    createNewProvisioningOrder: false,
    pac: '',
    tels,
    keepSim: null
  } as unknown as MigrateInCommand;
};

const createPortInCommand: (command: SimCommandCreate) => PortInCommand = (command: SimCommandCreate) => {
  const tels =
    command.stockParams && command.stockParams.length === command.stockIds.length
      ? [..._.map(command.stockParams, (item) => item || null)]
      : Array(command.stockIds.length).fill(null);
  return {
    ...getBasicCommandSettings(command, 'port-in'),
    definitionAlias: 'port-in',
    boltOns: [],
    boltOnsString: '',
    createNewProvisioningOrder: false,
    pac: '',
    tels
  } as unknown as PortInCommand;
};

const createProvisionCommand: (command: SimCommandCreate) => ProvisionCommand = (command: SimCommandCreate) => {
  const tels =
    command.stockParams && command.stockParams.length === command.stockIds.length
      ? [..._.map(command.stockParams, (item) => item || null)]
      : Array(command.stockIds.length).fill(null);
  return {
    ...getBasicCommandSettings(command, 'provision'),
    definitionAlias: 'provision',
    boltOns: [],
    boltOnsString: '',
    createNewProvisioningOrder: false,
    pac: '',
    provisionType: 'new',
    tels,
    stock: []
  };
};

const createMigrateOutCommand: (command: SimCommandCreate) => MigrateOutCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'migrate-out'),
    definitionAlias: 'migrate-out',
    keepSim: null
  } as unknown as MigrateOutCommand;
};

const createPortOutCommand: (command: SimCommandCreate) => PortOutCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'port-out'),
    definitionAlias: 'port-out',
    targetNetwork: ''
  };
};

const createUnprovisionCommand: (command: SimCommandCreate) => UnprovisionCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'unprovision'),
    definitionAlias: 'unprovision'
  };
};

const createChangeProvisionCommand: (command: SimCommandCreate) => ChangeProvisionCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'change'),
    definitionAlias: 'change-provision',
    boltOns: [],
    boltOnsString: '',
    createNewProvisioningOrder: false,
    pac: '',
    temporalBoltOns: []
  };
};

const createOrangeModifyBundleCommand: (command: SimCommandCreate) => OrangeModifyBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'modify'),
    definitionAlias: 'orange-modify-bundle',
    networkId: 192
  };
};

const createOrangeResetBundleCommand: (command: SimCommandCreate) => OrangeResetBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'orange-reset-bundle'),
    definitionAlias: 'orange-reset-bundle'
  };
};

const createOrangeTopupCommand: (command: SimCommandCreate) => OrangeTopupCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'top up'),
    definitionAlias: 'orange-top-up',
    networkId: 192
  };
};

const createOrangeUsageCommand: (command: SimCommandCreate) => OrangeUsageCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'request usage on'),
    definitionAlias: 'orange-usage',
    networkId: 192
  };
};

const CreateBundleUsageCommand: (command: SimCommandCreate) => OrangeLightBundleUsage = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'request usage on'),
    definitionAlias: 'bundle-usage',
    networkId: 234
  };
};

const createEsimsmartUsageCommand: (command: SimCommandCreate) => EsimsmartUsageCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'request usage on'),
    definitionAlias: 'esimsmart-usage',
    networkId: command.networkId
  } as unknown as EsimsmartUsageCommand;
};

const createTopupCommand: (command: SimCommandCreate) => TopupCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'top up'),
    definitionAlias: 'top-up',
    networkId: command.networkId
  } as unknown as TopupCommand;
};

const createSimStatusReportCommand: (command: SimCommandCreate) => SimStatusReportCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'status report'),
    definitionAlias: 'sim-status-report'
  };
};

export const createEditPoCommand: (command: SimCommandCreate) => EditPoCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'change the purchase order number for'),
    definitionAlias: 'edit-po',
    poNumber1: null,
    poNumber2: null,
    poNumber3: null,
    poNumber4: null,
    poNumber5: null
  };
};

export const createReconfigureSimProfileCommand: (command: SimCommandCreate) => ReconfigureSimProfileCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'reconfigure the SIM profile for'),
    definitionAlias: 'reconfigure-sim-profile',
    order: null,
    smsAlerts: [],
    services: [],
    spendCap: undefined
  };
};

export const createBarServiceConfigCommand: (command: SimCommandCreate) => BarServiceConfigCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'reconfigure the service configuration'),
    definitionAlias: 'bar-service-config',
    services: []
  };
};

export const createBtIotUpdateDetailsCommand: (command: SimCommandCreate) => BtIotUpdateDetailsCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'bt-iot-update-details'),
    definitionAlias: 'bt-iot-update-details',
    actionDateUtc: new Date(),
    imeiLock: false,
    viewedSim: false
  };
};

const getBasicCommandSettings: (command: SimCommandCreate, actionName: string) => BaseCommand = (
  command,
  actionName
) => {
  const { accountId, jsonAlias, networkId, stockIds, tabName } = command;
  let key = jsonAlias;
  if (networkId) {
    key += `_${networkId}`;
  }

  if (accountId) {
    key += `_${accountId}`;
  }

  return {
    definition: {
      actionName,
      jsonAlias,
      tabName
    },
    confirm: false,
    key,
    accountId,
    networkId,
    stockIds: stockIds || [],
    showValidationErrors: false
  };
};

const createConfigureSmsAlertsCommand: (command: SimCommandCreate) => ConfigureSmsAlertsCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'Configure SMS'),
    definitionAlias: 'configure-sms-alerts',
    emailTo: '',
    emailCc: '',
    emailBcc: '',
    additionalTel: '',
    alertLevel: 100,
    warningLevel: 80,
    alertMessage:
      'You’ve used {0} of your monthly allowance, it will renew on the 1st of the following month. You will now be charged for any additional usage. To avoid additional charges please contact the support team.',
    warningMessage:
      'You’ve exceeded {0} of your monthly allowance, it will renew on the 1st of the following month. We will let you know when you’ve used 100% after that you will be charged for any additional usage. To increase your allowance please contact the support team.',
  };
};

const createDeconfigureSmsAlertsCommand: (command: SimCommandCreate) => DeconfigureSmsAlertsCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'deconfigure'),
    definitionAlias: 'remove-sms-alerts'
  };
};

export const getDefaultSubmissionState: () => SubmissionState = () => {
  return {
    name: '',
    actionDate: getDaysTime(1),
    delayAction: false,
    isLoading: false,
    params: {
      commandType: '',
      selectedStockSerialNumbers: []
    },
    simCommands: [],
    simDefinitionAliases: []
  };
};

// orange bundle commands
const createCreateBundleCommand: (command: SimCommandCreate) => CreateBundleCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'add a bundle to'),
    definitionAlias: 'orange-create-bundle',
    networkId: 192,
    bundlePeriodValue: 1,
    bundlePeriodUnit: 'month',
    endOfBundleAction: 'cut-off',
    setStartDate: false,
    willExpire: false,
    startDate: getUtcDaysTime(1),
    endDate: getMonthsTime(1)
  };
};

const createRemoveBundleCommand: (command: SimCommandCreate) => OrangeRemoveBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'remove the bundle from'),
    definitionAlias: 'orange-remove-bundle'
  };
};

export const createRefreshConnectionCommand: (command: SimCommandCreate) => RefreshConnectionCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'refresh connection'),
    definitionAlias: 'sim-refresh'
  };
};

// esimsmart bundle commands
const createEsimsmartCreateBundleCommand: (command: SimCommandCreate) => EsimsmartCreateBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'add a bundle to'),
    definitionAlias: 'esimsmart-create-bundle',
    networkId: command.networkId,
    period: 'monthly',
    bundleSize: 0,
    expirationDate: getMonthsTime(1),
    template: 0,
    setStartDate: false,
    startDate: getUtcDaysTime(0),
    willExpire: false
  } as unknown as EsimsmartCreateBundleCommand;
};

const createEsimsmartChangeBundleCommand: (command: SimCommandCreate) => EsimSmartChangeBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'change bundle of'),
    definitionAlias: 'esimsmart-change-bundle',
    networkId: command.networkId,
    period: 'monthly',
    bundleSize: 0,
    expirationDate: null,
    template: 0,
    setStartDate: false,
    startDate: null,
    willExpire: false
  } as unknown as EsimSmartChangeBundleCommand;
};

const createEsimsmartRemoveBundleCommand: (command: SimCommandCreate) => EsimsmartRemoveBundleCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'remove the bundle from'),
    definitionAlias: 'esimsmart-remove-bundle'
  };
};

const createAssignAlertManager: (command: SimCommandCreate) => AssignAlertManagerCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'configure an alert manager for'),
    definitionAlias: 'assign-alert-manager'
  };
};

const createRemoveAlertManager: (command: SimCommandCreate) => RemoveAlertManagerCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'remove alert manager for'),
    definitionAlias: 'remove-alert-manager'
  };
};

const createChangeAccountCommand: (command: SimCommandCreate) => ChangeAccountCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'change the account of'),
    definitionAlias: 'change-account',
    moveToAccountId: 0
  };
};

const createChangeAccountRefCommand: (command: SimCommandCreate) => ChangeAccountRefCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'change the account reference of'),
    definitionAlias: 'change-self-cert-account-ref',
    accountReference: ''
  };
};

export const createExportEsimCommand: (command: SimCommandCreate) => ExportEsimCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'export eSims for'),
    definitionAlias: 'export-esim',
    emailAddress: ''
  };
};

const CreateConfigureThresholdCommand: (command: SimCommandCreate) => ConfigureThresholdCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'configure threshold'),
    definitionAlias: 'configure-threshold',
    shutdownThreshold: 0
  };
};

const CreateRemoveImeiLockCommand: (command: SimCommandCreate) => RemoveImeiLockCommand = (
  command: SimCommandCreate
) => {
  return {
    ...getBasicCommandSettings(command, 'remove-imei-lock'),
    definitionAlias: 'remove-imei-lock'
  };
};

const CreateResetImeiLockCommand: (command: SimCommandCreate) => ResetImeiLockCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'reset-imei-lock'),
    definitionAlias: 'reset-imei-lock'
  };
};

const CreateImeiLockCommand: (command: SimCommandCreate) => CreateImeiLockCommand = (command: SimCommandCreate) => {
  return {
    ...getBasicCommandSettings(command, 'create-imei-lock'),
    definitionAlias: 'create-imei-lock'
  };
};

type CommandBuilder = (simCommand: SimCommandCreate) => SubmissionCommand;

export class CommandService {
  private commandBuilders: HashMap<CommandBuilder> = {};

  public registerCommand(id: string, commandBuilder: CommandBuilder) {
    this.commandBuilders[id] = (simCommand: SimCommandCreate) => {
      return commandBuilder(simCommand);
    };
  }

  public createCommandEntryTab = (command: SimCommandCreate) => {
    const builder = this.commandBuilders[command.jsonAlias];
    if (!builder) {
      log.error(`Builder not found for ${command.jsonAlias}`);
      throw new Error(`Builder not found for ${command.jsonAlias}`);
    }

    return builder(command);
  };
}

export const commandService = new CommandService();
commandService.registerCommand('basic-bar', createBasicBarCommand);
commandService.registerCommand('basic-unbar', createBasicUnbarCommand);
commandService.registerCommand('check-serial', createCheckSerialCommand);
commandService.registerCommand('change-provision', createChangeProvisionCommand);
commandService.registerCommand('configure-sms-alerts', createConfigureSmsAlertsCommand);
commandService.registerCommand('remove-sms-alerts', createDeconfigureSmsAlertsCommand);
commandService.registerCommand('lift-spend-cap', createLiftCapCommand);
commandService.registerCommand('migrate-in', createMigrateInCommand);
commandService.registerCommand('migrate-out', createMigrateOutCommand);
commandService.registerCommand('o2-detailed-bar', createO2DetailedBarCommand);
commandService.registerCommand('o2-detailed-unbar', createO2DetailedUnbarCommand);
commandService.registerCommand('esimsmart-detailed-bar', createEsimSmartDetailedBarCommand);
commandService.registerCommand('esimsmart-detailed-unbar', createEsimSmartDetailedUnbarCommand);
commandService.registerCommand('orange-usage', createOrangeUsageCommand);
commandService.registerCommand('esimsmart-usage', createEsimsmartUsageCommand);
commandService.registerCommand('port-in', createPortInCommand);
commandService.registerCommand('port-out', createPortOutCommand);
commandService.registerCommand('provision', createProvisionCommand);
commandService.registerCommand('sim-status-report', createSimStatusReportCommand);
commandService.registerCommand('sim-swap', createSwapCommand);
commandService.registerCommand('unprovision', createUnprovisionCommand);
commandService.registerCommand('voda-detailed-bar', createVodaDetailedBarCommand);
commandService.registerCommand('voda-detailed-unbar', createVodaDetailedUnbarCommand);
commandService.registerCommand('edit-po', createEditPoCommand);
commandService.registerCommand('reconfigure-sim-profile', createReconfigureSimProfileCommand);
commandService.registerCommand('bar-service-config', createBarServiceConfigCommand);
commandService.registerCommand('export-esim', createExportEsimCommand);
commandService.registerCommand('configure-spend-cap', createSpendCapSubmission);
commandService.registerCommand('remove-spend-cap', removeSpendCapSubmission);
commandService.registerCommand('msisdn-swap', createMsisdnSwapCommand);
commandService.registerCommand('send-sms', CreateSendSmsSubmission);
commandService.registerCommand('bundle-usage', CreateBundleUsageCommand);
commandService.registerCommand('configure-threshold', CreateConfigureThresholdCommand);

// orange bundle commands
commandService.registerCommand('orange-create-bundle', createCreateBundleCommand);
commandService.registerCommand('orange-modify-bundle', createOrangeModifyBundleCommand);
commandService.registerCommand('orange-reset-bundle', createOrangeResetBundleCommand);
commandService.registerCommand('orange-remove-bundle', createRemoveBundleCommand);
commandService.registerCommand('orange-top-up', createOrangeTopupCommand);

// esimsmart bundle commands
commandService.registerCommand('esimsmart-create-bundle', createEsimsmartCreateBundleCommand);

commandService.registerCommand('esimsmart-change-bundle', createEsimsmartChangeBundleCommand);

commandService.registerCommand('sim-refresh', createRefreshConnectionCommand);
commandService.registerCommand('esimsmart-remove-bundle', createEsimsmartRemoveBundleCommand);
commandService.registerCommand('top-up', createTopupCommand);

// alert manager commands
commandService.registerCommand('assign-alert-manager', createAssignAlertManager);
commandService.registerCommand('remove-alert-manager', createRemoveAlertManager);

// bundle monitor commands
// account move commands
commandService.registerCommand('change-account', createChangeAccountCommand);

// change set cert account reference command
commandService.registerCommand('change-self-cert-account-ref', createChangeAccountRefCommand);

// configure bundle commands
commandService.registerCommand('configure-bundle-control', configureBundleControlSubmission);
commandService.registerCommand('configure-fair-usage', configureFairUsageSubmission);
commandService.registerCommand('remove-bundle-control', RemoveBundleControlSubmission);

// imei lock commands
commandService.registerCommand('remove-imei-lock', CreateRemoveImeiLockCommand);
commandService.registerCommand('reset-imei-lock', CreateResetImeiLockCommand);
commandService.registerCommand('create-imei-lock', CreateImeiLockCommand);
